import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Container,
  Grid2,
  Chip,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  ChevronLeft as LeftChevronIcon,
  ChevronRight as RightChevronIcon,
} from "@mui/icons-material";
import CustomStepper from "../../../../custom-components/CustomStepper";
import {
  getStepperSteps,
  getStepIndex,
} from "../../../../../utils/stepperConfig";
import { useSelector } from "react-redux";

// Helper function to get priority color
const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high":
      return "#f44336";
    case "medium":
      return "#ff9800";
    case "low":
      return "#4caf50";
    default:
      return "#9e9e9e";
  }
};

const TestCaseImportedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    automationType,
    locationKey,
    testCases,
    currentTestCaseIndex = 0,
    totalTestCases = 35,
    currentStepIndex: stateStepIndex = 0,
  } = location.state || {};

  // Capture data from redux store using selector
  const preferences = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "automationPreferences"
      ] || {}
  );

  // const testCases = useSelector(
  //   (state) =>
  //     state["testAutomation"][locationKey]["webAutomation"][
  //       "testCasesUploaded"
  //     ] || null
  // );

  const environment = useSelector(
    (state) =>
      state["testAutomation"][locationKey]["webAutomation"][
        "environmentData"
      ] || {}
  );

  // Debug: Log the received data
  console.log("Received test cases data:", testCases);
  console.log("Current test case index:", currentTestCaseIndex);
  console.log("Total test cases:", totalTestCases);

  const currentTestCase = testCases?.[currentTestCaseIndex] || {
    testCaseId: "No test case available",
    title: "No test case available",
    testSteps: "No test steps available",
    expectedResult: "No expected result available",
    moduleName: "General",
    priority: "Medium",
    remarks: "No remarks available",
  };

  // Get dynamic steps based on framework selection
  const progressSteps = getStepperSteps(preferences);
  const currentStepIndex =
    stateStepIndex !== undefined
      ? stateStepIndex
      : getStepIndex(progressSteps, "Test Case Imported");

  const handleBack = () => {
    navigate("/dashboard/test-automation/test-cases", {
      state: { automationType, preferences, environment, testCases },
    });
  };

  const handleNext = () => {
    const nextStep = progressSteps[currentStepIndex + 1];
    const nextStepPath = getStepPath(nextStep);

    navigate(nextStepPath, {
      state: {
        automationType,
        preferences,
        environment,
        testCases,
        currentTestCaseIndex,
        totalTestCases,
        currentStep: nextStep,
        currentStepIndex: currentStepIndex + 1,
      },
    });
  };

  // Helper function to get route path based on step name
  const getStepPath = (stepName) => {
    const stepPaths = {
      "Test Case Imported":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported",
      "Introduce SUT":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/introduce-sut",
      "Generate Locators":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-locators",
      "Generate Page Objects":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-page-objects",
      "Generate Test Files":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-test-files",
    };
    return (
      stepPaths[stepName] ||
      "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported"
    );
  };

  const handlePreviousTestCase = () => {
    if (currentTestCaseIndex > 0) {
      navigate(
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported",
        {
          state: {
            automationType,
            preferences,
            environment,
            testCases,
            currentTestCaseIndex: currentTestCaseIndex - 1,
            totalTestCases,
          },
        }
      );
    }
  };

  const handleNextTestCase = () => {
    if (currentTestCaseIndex < totalTestCases - 1) {
      navigate(
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported",
        {
          state: {
            automationType,
            preferences,
            environment,
            testCases,
            currentTestCaseIndex: currentTestCaseIndex + 1,
            totalTestCases,
          },
        }
      );
    }
  };

  // Show message if no test cases are available
  if (!testCases || testCases.length === 0) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#F6F3F2",
          display: "flex",
          flexDirection: "column",
          py: 4,
        }}
      >
        <Container maxWidth="lg" sx={{ width: "100%", flex: 1 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              mb: 4,
              fontWeight: "bold",
              color: "#333",
              fontSize: "28px",
              fontFamily: "Manrope",
            }}
          >
            Test Case Imported
          </Typography>
          <Card
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              mb: 4,
              maxWidth: "100%",
            }}
          >
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Typography
                variant="h6"
                sx={{ color: "#666", fontFamily: "Manrope" }}
              >
                No test cases available. Please go back and upload test cases
                first.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#F6F3F2",
        display: "flex",
        flexDirection: "column",
        py: 4,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{ width: "100%", flex: 1, overflow: "hidden", px: 2 }}
      >
        {/* Custom Stepper */}
        <Box sx={{ mb: 4, width: "100%", overflow: "hidden" }}>
          <CustomStepper
            steps={progressSteps}
            activeStep={currentStepIndex}
            maxWidth="100%"
            containerWidth="100%"
            activeColor="#1976d2"
            completedColor="#40a9ff"
            pendingColor="#d1d1d1"
            showNextStep={true}
            showStepCount={true}
            title="Now"
            inProgressText="(In Progress)"
            completedText="(Completed)"
            nextText="Next"
            completedNextText="Completed"
            stepText="Step"
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          />
        </Box>

        {/* Page Title - Outside the card */}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            mb: { xs: 2, sm: 3, md: 4 },
            fontWeight: "bold",
            color: "#333",
            fontSize: { xs: "20px", sm: "24px", md: "28px" },
            fontFamily: "Manrope",
            lineHeight: { xs: 1.3, sm: 1.4 },
          }}
        >
          Test Case Imported
        </Typography>

        {/* Main Content Card */}
        <Card
          sx={{
            backgroundColor: "white",
            borderRadius: { xs: "4px", sm: "8px" },
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            mb: { xs: 2, sm: 3, md: 4 },
            maxWidth: "100%",
            border: "1px solid #E0E0E0",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Test Case Details - Four Fields Per Row Layout */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 3,
                mb: 4,
              }}
            >
              {/* First Row - 4 fields */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                {/* Test Case Title */}
                <Box sx={{ flex: 1, minWidth: "200px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontFamily: "Manrope",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    Test Case Title
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontFamily: "Manrope",
                      color: "#333",
                      fontSize: "16px",
                      lineHeight: 1.4,
                    }}
                  >
                    {currentTestCase.title}
                  </Typography>
                </Box>

                {/* Test Case ID */}
                <Box sx={{ flex: 1, minWidth: "150px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontFamily: "Manrope",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    Test Case ID
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Manrope",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    {currentTestCase.testCaseId}
                  </Typography>
                </Box>

                {/* Module Name */}
                <Box sx={{ flex: 1, minWidth: "150px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontFamily: "Manrope",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    Module Name
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Manrope",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    {currentTestCase.moduleName}
                  </Typography>
                </Box>

                {/* Priority */}
                <Box sx={{ flex: 1, minWidth: "120px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontFamily: "Manrope",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    Priority
                  </Typography>
                  <Chip
                    label={currentTestCase.priority}
                    size="small"
                    sx={{
                      backgroundColor: getPriorityColor(
                        currentTestCase.priority
                      ),
                      color: "#fff",
                      fontFamily: "Manrope",
                      fontSize: "12px",
                      fontWeight: 500,
                      height: "24px",
                    }}
                  />
                </Box>
              </Box>

              {/* Second Row - Remaining fields */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                {/* Expected Result */}
                <Box sx={{ flex: 1, minWidth: "200px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontFamily: "Manrope",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    Expected Result
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Manrope",
                      color: "#333",
                      fontSize: "14px",
                      lineHeight: 1.4,
                    }}
                  >
                    {currentTestCase.expectedResult}
                  </Typography>
                </Box>

                {/* Remarks */}
                <Box sx={{ flex: 1, minWidth: "200px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      mb: 1,
                      fontFamily: "Manrope",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  >
                    Remarks
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Manrope",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    {currentTestCase.remarks || "No remarks"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Full Width Sections */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  mb: 2,
                  fontFamily: "Manrope",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Test Steps
              </Typography>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Manrope",
                    lineHeight: 1.6,
                    color: "#333",
                    fontSize: "14px",
                    whiteSpace: "pre-line",
                    margin: 0,
                  }}
                >
                  {currentTestCase.testSteps}
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  mb: 2,
                  fontFamily: "Manrope",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Expected Result
              </Typography>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Manrope",
                    lineHeight: 1.6,
                    color: "#333",
                    fontSize: "14px",
                    whiteSpace: "pre-line",
                    margin: 0,
                  }}
                >
                  {currentTestCase.expectedResult}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Footer Navigation */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "transparent",
            borderRadius: "0 0 8px 8px",
            padding: "16px 0",
          }}
        >
          {/* Back Button */}
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={handleBack}
            sx={{
              width: 105,
              height: 40,
              border: "2px solid #0094FB",
              borderRadius: "8px",
              backgroundColor: "transparent",
              padding: "8px 16px",
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "24px",
              letterSpacing: "0.15px",
              color: "#0094FB",
              textTransform: "none",
              "&:hover": {
                border: "2px solid #0094FB",
                backgroundColor: "rgba(0, 148, 251, 0.04)",
              },
              transition: "all 0.3s ease-out",
            }}
          >
            Back
          </Button>

          {/* Next Button */}
          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={handleNext}
            sx={{
              width: 105,
              height: 40,
              backgroundColor: "#0094FB",
              borderRadius: "8px",
              padding: "8px 16px",
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "24px",
              letterSpacing: "0.15px",
              color: "white",
              textTransform: "none",
              border: "none",
              "&:hover": {
                backgroundColor: "#0078D4",
              },
              transition: "all 0.3s ease-out",
            }}
          >
            Next
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default TestCaseImportedPage;
